import React, { useState, useEffect } from 'react';
import { Sparkles, Trophy, Zap, X } from 'lucide-react';
import { useProfileImage } from '../../profile/hooks/useProfileImage';

// Shared prediction result UI — extracted from PredictionsPage.jsx so the
// tournament schedule's per-match "Predict" button can render the exact same
// animated result card without duplicating ~150 lines of markup.

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function avatarGradient(id) {
  const gradients = [
    'from-blue-500 to-indigo-600',
    'from-purple-500 to-pink-600',
    'from-emerald-500 to-teal-600',
    'from-orange-500 to-red-600',
    'from-cyan-500 to-blue-600',
    'from-rose-500 to-pink-600',
    'from-amber-500 to-orange-600',
    'from-violet-500 to-purple-600',
  ];
  const hash = (id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
}

export function PlayerAvatar({ name, id, size = 'md', src }) {
  const { imageUrl } = useProfileImage(id, src);
  const sz = size === 'lg' ? 'w-16 h-16 text-xl' : size === 'sm' ? 'w-8 h-8 text-xs' : 'w-11 h-11 text-sm';
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={`${sz} rounded-full object-cover flex-shrink-0 shadow-lg`}
      />
    );
  }
  return (
    <div className={`${sz} rounded-full bg-gradient-to-br ${avatarGradient(id)} flex items-center justify-center font-bold text-white flex-shrink-0 shadow-lg`}>
      {initials(name)}
    </div>
  );
}

export function ConfidenceBadge({ confidence }) {
  const map = {
    high: { label: 'High Confidence', cls: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' },
    low:  { label: 'Low Confidence',  cls: 'bg-amber-500/20 text-amber-300 border border-amber-500/40' },
    none: { label: 'No Data',         cls: 'bg-gray-500/20 text-gray-300 border border-gray-500/40' },
  };
  const c = map[confidence] || map.none;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${c.cls}`}>
      {c.label}
    </span>
  );
}

export function ProbabilityBar({ pct, side, animating }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!animating) { setWidth(0); return; }
    const t = setTimeout(() => setWidth(pct), 80);
    return () => clearTimeout(t);
  }, [pct, animating]);

  const isWinner = pct >= 50;
  const barColor = side === 'left'
    ? (isWinner ? 'from-blue-500 to-cyan-400' : 'from-blue-800 to-blue-700')
    : (isWinner ? 'from-rose-500 to-orange-400' : 'from-rose-800 to-rose-700');

  return (
    <div className="h-2.5 rounded-full bg-white/10 overflow-hidden w-full">
      <div
        className={`h-full rounded-full bg-gradient-to-r ${barColor} transition-all duration-1000 ease-out`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

export function PredictionResultCard({ result, player1, player2, player1Partner, player2Partner, onReset, sportName }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 50); return () => clearTimeout(t); }, []);

  const p1Wins = result.team1WinPct >= result.team2WinPct;
  const isDefaulted = result.confidence === 'none';

  return (
    <div className={`transition-all duration-500 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      {/* Deliberately a dark card in every theme — the white/cyan/rose type and
          glow effects are tuned for a dark ground. Kept fully opaque (not /80)
          so it doesn't wash out to mid-grey over the light themes' white page
          background, which would make the dimmed text unreadable. */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-blue-950 backdrop-blur-sm shadow-2xl">

        {/* Glow effects */}
        <div className={`absolute inset-0 opacity-20 pointer-events-none transition-opacity duration-1000 ${show ? 'opacity-20' : 'opacity-0'}`}>
          <div className="absolute left-0 top-0 w-1/2 h-full bg-gradient-to-r from-blue-500 to-transparent" />
          <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-rose-500 to-transparent" />
        </div>

        {/* Header */}
        <div className="relative px-6 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-amber-400" />
            <span className="text-xs font-semibold text-amber-400 uppercase tracking-widest">AI Prediction</span>
          </div>
          <div className="flex items-center gap-2">
            <ConfidenceBadge confidence={result.confidence} />
            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/60 uppercase tracking-wider">
              {result.gameType}
            </span>
            {onReset && (
              <button onClick={onReset} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* No history warning */}
        {isDefaulted && (
          <div className="mx-6 mb-4 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-start gap-2.5">
            <Sparkles size={13} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-300/80 leading-relaxed">
              One or more players have no {sportName ? `${sportName} ` : ''}match history in the prediction model. Results are unreliable.
            </p>
          </div>
        )}

        {/* Players + bar */}
        <div className="relative px-6 pb-6">
          <div className="flex items-center gap-4">

            {/* Team 1 */}
            <div className={`flex-1 flex flex-col items-center gap-3 transition-all duration-700 ${show ? 'opacity-100' : 'opacity-0'}`}>
              {player1Partner ? (
                <div className="flex items-center justify-center gap-1">
                  <PlayerAvatar name={player1.name} id={player1.id} size="lg" src={player1.avatar} />
                  <PlayerAvatar name={player1Partner.name} id={player1Partner.id} size="lg" src={player1Partner.avatar} />
                </div>
              ) : (
                <PlayerAvatar name={player1.name} id={player1.id} size="lg" src={player1.avatar} />
              )}
              <div className="text-center">
                <p className={`font-bold text-lg leading-tight ${p1Wins ? 'text-white' : 'text-white/50'}`}>
                  {player1.name}{player1Partner ? ` & ${player1Partner.name}` : ''}
                </p>
              </div>
              <div className="text-center">
                <span className={`text-4xl font-black tabular-nums transition-all duration-1000 ${p1Wins ? 'text-cyan-300 drop-shadow-[0_0_12px_rgba(34,211,238,0.6)]' : 'text-white/30'}`}>
                  {result.team1WinPct}%
                </span>
              </div>
              <div className="w-full">
                <ProbabilityBar pct={result.team1WinPct} side="left" animating={show} />
              </div>
              {p1Wins && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/40">
                  <Trophy size={12} className="text-cyan-300" />
                  <span className="text-xs font-bold text-cyan-300">FAVOURED</span>
                </div>
              )}
            </div>

            {/* VS divider */}
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className="w-px h-8 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
              <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <span className="text-xs font-black text-white/40 tracking-tighter">VS</span>
              </div>
              <div className="w-px h-8 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
            </div>

            {/* Team 2 */}
            <div className={`flex-1 flex flex-col items-center gap-3 transition-all duration-700 delay-100 ${show ? 'opacity-100' : 'opacity-0'}`}>
              {player2Partner ? (
                <div className="flex items-center justify-center gap-1">
                  <PlayerAvatar name={player2.name} id={player2.id} size="lg" src={player2.avatar} />
                  <PlayerAvatar name={player2Partner.name} id={player2Partner.id} size="lg" src={player2Partner.avatar} />
                </div>
              ) : (
                <PlayerAvatar name={player2.name} id={player2.id} size="lg" src={player2.avatar} />
              )}
              <div className="text-center">
                <p className={`font-bold text-lg leading-tight ${!p1Wins ? 'text-white' : 'text-white/50'}`}>
                  {player2.name}{player2Partner ? ` & ${player2Partner.name}` : ''}
                </p>
              </div>
              <div className="text-center">
                <span className={`text-4xl font-black tabular-nums transition-all duration-1000 ${!p1Wins ? 'text-rose-300 drop-shadow-[0_0_12px_rgba(251,113,133,0.6)]' : 'text-white/30'}`}>
                  {result.team2WinPct}%
                </span>
              </div>
              <div className="w-full">
                <ProbabilityBar pct={result.team2WinPct} side="right" animating={show} />
              </div>
              {!p1Wins && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/20 border border-rose-500/40">
                  <Trophy size={12} className="text-rose-300" />
                  <span className="text-xs font-bold text-rose-300">FAVOURED</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Matches PredictionResultCard's dark treatment — see the note there.
export function PredictingLoader({ player1, player2 }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-blue-950 p-8 flex flex-col items-center gap-5">
      <div className="flex items-center gap-6">
        <div className="flex flex-col items-center gap-2 opacity-60">
          <PlayerAvatar name={player1?.name} id={player1?.id} size="lg" />
          <p className="text-sm font-semibold text-white/60">{player1?.name}</p>
        </div>
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-14 h-14">
            <div className="absolute inset-0 rounded-full border-2 border-blue-500/20 animate-ping" />
            <div className="absolute inset-2 rounded-full border-2 border-blue-500/40 animate-ping animation-delay-150" />
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Zap size={20} className="text-white animate-pulse" />
            </div>
          </div>
          <p className="text-xs text-white/40 animate-pulse">Analysing match data…</p>
        </div>
        <div className="flex flex-col items-center gap-2 opacity-60">
          <PlayerAvatar name={player2?.name} id={player2?.id} size="lg" />
          <p className="text-sm font-semibold text-white/60">{player2?.name}</p>
        </div>
      </div>
      <div className="flex gap-1">
        {[0,1,2,3,4].map(i => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: `${i * 100}ms` }} />
        ))}
      </div>
    </div>
  );
}
